<template>
  <LoadingSpinner v-if="projectsLoading || subcontractorsLoading" />
  <div v-else class="create-work-order">
    <PageHeader title="Add Work Order" />

    <div class="mx-auto max-w-7xl">
      <WorkOrderForm
        :project-id="projectId"
        :projects="projects"
        :subcontractors="subcontractors"
        :save-function="createWorkOrder"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { WorkOrder } from '@/models/workOrder'
import WorkOrderForm from '../components/WorkOrderForm.vue'
import PageHeader from '@/components/app/AppPageHeader.vue'
import { useHead } from '@unhead/vue'
import PorjectService from '@/services/ProjectService'
import { useRouter, useRoute } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import BudgetService from '@/services/BudgetService'
import SubcontractorService from '@/services/SubcontractorService'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { PROJECT_STATUS } from '@/models/project'

useHead({ title: 'Add Work Order' })

const $router = useRouter()
const { notify } = useNotification()
const projectId = useRoute().query.projectId as string || ''
const queryClient = useQueryClient()

const { data: projects, isLoading:projectsLoading } = useQuery({
  queryKey: ['projects'],
  queryFn: () => new PorjectService().find_by('status', '==', PROJECT_STATUS.IN_PROGRESS).then((projects) => projects.map((project) => ({ value: project.id, label: project.name, status: project.status })))
})

const { data: subcontractors, isLoading:subcontractorsLoading } = useQuery({
  queryKey: ['subcontractors'],
  queryFn: () => new SubcontractorService().all().then((subcontractors) => subcontractors.map((subcontractor) => ({ value: subcontractor.id, label: subcontractor.name })))
})


const { mutateAsync: createWorkOrder } = useMutation({
  mutationFn: (workOrder: WorkOrder) => new BudgetService().addWorkOrder(workOrder.budgetId, workOrder),
  onSuccess: (createdWorkOrder: WorkOrder) => {
    queryClient.setQueryData(['workOrder', createdWorkOrder.id], createdWorkOrder)
    queryClient.invalidateQueries({ queryKey: ['workOrders'] })
    notify({
      type: 'success',
      title: 'Work order created',
      text: 'Work order created successfully'
    })
    $router.push({ name: 'ShowWorkOrder', params: { id: createdWorkOrder.id }, hash: '#showConfirmation' })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error',
      text: error.message || 'An error occurred while trying to create the work order'
    })
  }
})
</script>
